import { useEffect, useState, useCallback } from "react";
import { Wrench, MapPin, Phone, MessageCircle, Search, Clock, Navigation, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { waLink } from "@/lib/constants";

function CentreCard({ c }) {
  const digits = (c.phone || "").replace(/[^0-9+]/g, "");
  return (
    <div data-testid="centre-card" className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 hover:border-primary/40 transition-all duration-300 animate-fadeup">
      <h3 className="font-heading font-semibold text-base sm:text-lg break-words flex items-center gap-2">
        <Wrench className="w-4 h-4 text-primary shrink-0" /> {c.name}
        {c.brand && <span className="text-[10px] font-mono-num px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 shrink-0">{c.brand}</span>}
      </h3>
      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
        <MapPin className="w-3.5 h-3.5 text-primary/70 shrink-0" /> {c.city}, {c.state}
        {typeof c.distance_km === "number" && (
          <span data-testid="centre-distance" className="ml-1 text-[11px] font-mono-num px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{c.distance_km} km</span>
        )}
      </p>
      {c.address && <p className="text-sm text-muted-foreground/90 mt-2">{c.address}</p>}
      {c.services && <p className="text-xs text-muted-foreground mt-2"><span className="text-foreground font-medium">Services:</span> {c.services}</p>}
      {c.hours && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {c.hours}</p>}
      <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-border">
        <a data-testid="centre-call-btn" href={`tel:${digits}`}
           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-primary/50 hover:text-primary transition-all">
          <Phone className="w-3.5 h-3.5" /> Call
        </a>
        <a data-testid="centre-whatsapp-btn" href={waLink(c.phone, `Hi, I need reefer/AC repair support for my truck. (via Reefer Sathi)`)} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#25D366] text-white hover:brightness-110 transition-all">
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </a>
      </div>
    </div>
  );
}

export default function ServiceCentres() {
  const [centres, setCentres] = useState([]);
  const [states, setStates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "all", brand: "all", q: "" });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [nearby, setNearby] = useState(false);

  const fetchCentres = useCallback(async (loc) => {
    setLoading(true);
    try {
      const useLoc = loc !== undefined ? loc : coords;
      const params = { state: filters.state, brand: filters.brand, q: filters.q || undefined };
      if (useLoc) { params.lat = useLoc.lat; params.lon = useLoc.lon; }
      const r = await api.get("/service-centres", { params });
      setCentres(r.data.centres);
      setStates(r.data.states);
      setBrands(r.data.brands || []);
      setNearby(!!r.data.sorted_by_distance);
    } catch (e) {
      /* directory fetch failed; leave list empty */
    } finally { setLoading(false); }
  }, [filters, coords]);

  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location is not available on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: +pos.coords.latitude.toFixed(4), lon: +pos.coords.longitude.toFixed(4) };
        setCoords(loc);
        setLocating(false);
        fetchCentres(loc);
        toast.success("Showing repair centres nearest to you first.");
      },
      () => { setLocating(false); toast.error("Couldn't get your location. Please allow location access."); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const clearNearby = () => { setCoords(null); setNearby(false); fetchCentres(null); };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight">AC / Reefer Repair Centres</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Refrigeration &amp; AC repair support across India when a unit needs service on the road.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          data-testid="centre-near-me-btn"
          onClick={useMyLocation}
          disabled={locating}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {locating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Navigation className="w-4 h-4 mr-1.5" />}
          {locating ? "Locating…" : "Nearest to me"}
        </Button>
        {nearby && (
          <button data-testid="centre-clear-nearby" onClick={clearNearby} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
            Clear · sorted by distance
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-[170px_170px_1fr_auto] gap-3 mb-6 p-4 rounded-xl border border-border bg-card/40">
        <Select value={filters.brand} onValueChange={(v) => setFilters(f => ({ ...f, brand: v }))}>
          <SelectTrigger data-testid="centre-filter-brand"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All brands</SelectItem>
            {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.state} onValueChange={(v) => setFilters(f => ({ ...f, state: v }))}>
          <SelectTrigger data-testid="centre-filter-state"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All states</SelectItem>
            {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="centre-search" className="pl-9" placeholder="Search by name, city or service" value={filters.q}
                 onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && fetchCentres()} />
        </div>
        <Button data-testid="centre-search-btn" onClick={() => fetchCentres()} variant="secondary" className="rounded-lg"><Search className="w-4 h-4 mr-1.5" />Search</Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-16 text-center">Loading centres…</p>
      ) : centres.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <Wrench className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">No repair centres found for this filter.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {centres.map((c) => <CentreCard key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}
